/**
 * IntelligentInterview
 * Context-aware interview system that generates targeted questions
 * based on extracted product data from Phase 1-2 analyzers
 *
 * Reduces interview from 15-20 generic questions to 5-8 contextual questions
 */
export class IntelligentInterview {
    constructor() {
        this.extractedData = null;
        this.questions = [];
        this.currentQuestionIndex = 0;
    }

    /**
     * Initialize interview with product data
     * @param {Object} productData - Full product JSON from /products/{id}
     */
    async init(productData) {
        this.extractedData = this._parseExtractedData(productData);
        this.questions = this._generateQuestionFlow();
        return this.questions;
    }

    /**
     * Parse all extracted data from product JSON
     */
    _parseExtractedData(product) {
        return {
            basic: {
                productId: product.productId,
                name: product.name,
                description: product.description,
                targetAudience: product.targetAudience,
                features: product.features || []
            },
            urlAnalysis: product.urlAnalysis || null, // DV2-01
            proofElements: product.proofElements || null, // DV2-02
            competitiveIntel: product.competitiveIntelligence || null, // DV2-03
            businessModel: product.businessModel || null, // DV2-04
            personas: product.buyerPersonas || null, // DV2-05
            positioning: product.positioning || null // DV2-06
        };
    }

    /**
     * Generate contextual question flow based on extracted data
     */
    _generateQuestionFlow() {
        const questions = [];

        // 1. Validate extracted personas (if confidence is medium)
        if (this._hasPersonaData()) {
            const confidence = this.extractedData.personas.confidence || 0.7;

            if (confidence >= 0.5 && confidence < 0.9) {
                questions.push(this._createPersonaValidationQuestion());
            }

            // Follow-up: Buying triggers (always ask if persona exists)
            questions.push(this._createBuyingTriggerQuestion());

            // Follow-up: Objections
            questions.push(this._createObjectionQuestion());
        } else {
            // Gap-fill: No persona data
            questions.push(this._createPersonaGapFillQuestion());
        }

        // 2. Validate positioning (if exists)
        if (this._hasPositioningData()) {
            const confidence = this.extractedData.positioning.confidence || 0.8;

            if (confidence >= 0.5 && confidence < 0.9) {
                questions.push(this._createPositioningValidationQuestion());
            }
        } else {
            // Gap-fill: No positioning
            questions.push(this._createPositioningGapFillQuestion());
        }

        // 3. Proof elements check
        if (!this._hasProofData()) {
            questions.push(this._createProofGapFillQuestion());
        }

        // 4. Business model specific questions
        const businessModelQuestion = this._createBusinessModelQuestion();
        if (businessModelQuestion) {
            questions.push(businessModelQuestion);
        }

        // 5. Campaign goal (always ask)
        questions.push(this._createCampaignGoalQuestion());

        // 6. Constraints (always ask)
        questions.push(this._createConstraintsQuestion());

        return questions;
    }

    /**
     * Data availability checks
     */
    _hasPersonaData() {
        return this.extractedData.personas &&
               this.extractedData.personas.primaryPersona;
    }

    _hasPositioningData() {
        return this.extractedData.positioning &&
               this.extractedData.positioning.enemyName;
    }

    _hasProofData() {
        return this.extractedData.proofElements &&
               (this.extractedData.proofElements.testimonials?.length > 0 ||
                this.extractedData.proofElements.metrics?.length > 0);
    }

    /**
     * Question creation methods
     */
    _createPersonaValidationQuestion() {
        const persona = this.extractedData.personas.primaryPersona;
        const demographics = persona.demographics || {};

        return {
            key: 'personaValidation',
            type: 'validation',
            category: 'persona',
            text: `We generated this primary buyer persona:\n\n**${persona.name}**: ${persona.tagline}\n\n**Job Title**: ${demographics.jobTitle || 'Not specified'}\n**Industry**: ${demographics.industry || 'Not specified'}\n\nDoes this match your actual buyers? What would you change?`,
            quickReplies: [
                "Yes, that's accurate",
                "Close, but needs tweaks",
                "Not quite right",
                "Let me explain..."
            ],
            dataSource: 'Persona Builder (DV2-05)',
            confidence: this.extractedData.personas.confidence || 0.7
        };
    }

    _createBuyingTriggerQuestion() {
        const personaName = this.extractedData.personas?.primaryPersona?.name || 'your target buyer';

        return {
            key: 'buyingTrigger',
            type: 'deep-dive',
            category: 'persona-triggers',
            text: `For ${personaName}, what specific EVENT triggers them to search for a solution like yours?\n\nExample: "Client asks for data to back up coaching recommendations" or "Sales team misses quota for 2nd quarter in a row"`,
            quickReplies: [
                "Let me think and type it out..."
            ],
            required: true
        };
    }

    _createObjectionQuestion() {
        const personaName = this.extractedData.personas?.primaryPersona?.name || 'your target buyer';

        return {
            key: 'topObjection',
            type: 'deep-dive',
            category: 'persona-objections',
            text: `What's the #1 objection ${personaName} has before buying? What makes them hesitate?\n\nExample: "Don't have time to learn new tool" or "Worried about data security"`,
            quickReplies: [
                "Price concerns",
                "Time/complexity",
                "Trust/credibility",
                "Something else..."
            ],
            required: true
        };
    }

    _createPersonaGapFillQuestion() {
        return {
            key: 'buyer',
            type: 'gap-fill',
            category: 'persona',
            text: "Who's the target buyer making the purchasing decision? Be specific about their role and situation.",
            quickReplies: [
                "Individuals (consumers)",
                "Small business owners",
                "Agency owners/teams",
                "Enterprise buyers",
                "Let me explain..."
            ],
            required: true
        };
    }

    _createPositioningValidationQuestion() {
        const pos = this.extractedData.positioning;

        return {
            key: 'positioningValidation',
            type: 'validation',
            category: 'positioning',
            text: `We identified your competitive positioning:\n\n**Enemy**: ${pos.enemyName}\n**NOT**: ${pos.positioningStatement?.notThis || 'Traditional approach'}\n**IS**: ${pos.positioningStatement?.isThis || 'Your unique approach'}\n\nDoes this resonate? Is this how you'd position against alternatives?`,
            quickReplies: [
                "Yes, that's spot on",
                "Close enough",
                "Not quite right",
                "Let me refine..."
            ],
            dataSource: 'Positioning Engine (DV2-06)',
            confidence: this.extractedData.positioning.confidence || 0.8
        };
    }

    _createPositioningGapFillQuestion() {
        return {
            key: 'uniqueValue',
            type: 'gap-fill',
            category: 'positioning',
            text: 'What makes this different or better than alternatives? Complete this:\n\n"We are NOT [generic solution], we ARE [your unique approach]"',
            quickReplies: [
                "Let me think and type it out..."
            ],
            required: true
        };
    }

    _createProofGapFillQuestion() {
        return {
            key: 'proof',
            type: 'gap-fill',
            category: 'proof',
            text: 'Do you have a customer testimonial, case study, or specific result you can share? (Even one is valuable)\n\nExample: "Helped Agency X increase close rate by 34% in 60 days"',
            quickReplies: [
                "Yes, let me share it",
                "Not yet",
                "Skip this for now"
            ],
            required: false
        };
    }

    _createBusinessModelQuestion() {
        const businessModel = this.extractedData.businessModel;

        if (!businessModel || !businessModel.revenueModel) {
            // Gap-fill: Ask about business model
            return {
                key: 'businessModel',
                type: 'gap-fill',
                category: 'business-model',
                text: "How does the business model work? How do customers pay?",
                quickReplies: [
                    "One-time purchase",
                    "Monthly subscription",
                    "Usage-based pricing",
                    "Tiered plans",
                    "Let me explain..."
                ],
                required: true
            };
        }

        const revenueType = businessModel.revenueModel.type || '';

        // Service-specific question
        if (revenueType.toLowerCase().includes('service')) {
            return {
                key: 'serviceDelivery',
                type: 'business-specific',
                category: 'service-delivery',
                text: 'For your service, what does the delivery process look like?\n\nExample: "4-week program with weekly 1:1 sessions" or "Done-for-you setup in 7 days"',
                quickReplies: [
                    "Let me describe it..."
                ],
                required: false
            };
        }

        // SaaS-specific question
        if (revenueType.toLowerCase().includes('saas')) {
            return {
                key: 'saasOnboarding',
                type: 'business-specific',
                category: 'saas-onboarding',
                text: 'What does successful onboarding look like for new users? What\'s the "aha moment" they experience?',
                quickReplies: [
                    "Let me explain..."
                ],
                required: false
            };
        }

        return null; // No business-specific question
    }

    _createCampaignGoalQuestion() {
        return {
            key: 'campaignGoal',
            type: 'goal',
            category: 'campaign-objective',
            text: "What's the PRIMARY goal for this campaign?",
            quickReplies: [
                "Brand awareness",
                "Lead generation (email signups)",
                "Free trial signups",
                "Direct sales calls",
                "Product demo requests"
            ],
            required: true
        };
    }

    _createConstraintsQuestion() {
        return {
            key: 'constraints',
            type: 'constraint',
            category: 'constraints',
            text: "Last question: Are there any constraints or things we should NOT say in the campaign?",
            quickReplies: [
                "No constraints",
                "Let me specify..."
            ],
            required: false
        };
    }

    /**
     * Get next question in flow
     */
    getNextQuestion() {
        if (this.currentQuestionIndex >= this.questions.length) {
            return null; // Interview complete
        }

        const question = this.questions[this.currentQuestionIndex];
        this.currentQuestionIndex++;
        return question;
    }

    /**
     * Get total question count
     */
    getTotalQuestions() {
        return this.questions.length;
    }

    /**
     * Get current progress
     */
    getProgress() {
        return {
            current: this.currentQuestionIndex,
            total: this.questions.length,
            percentage: Math.round((this.currentQuestionIndex / this.questions.length) * 100)
        };
    }

    /**
     * Reset interview
     */
    reset() {
        this.currentQuestionIndex = 0;
    }
}
